import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { clearUser } from '../store/slice/userSlice';
import { authService } from '../services/authService';
import { notificationAPI } from '../api';
import { useAuth as useAuthHook } from '../hooks/useAuth';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    // If used outside provider, return hook directly
    return useAuthHook();
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const authState = useAuthHook();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const { data } = await notificationAPI.unreadCount();
      setUnreadCount(data.unread_count || 0);
    } catch (err) {}
  }, []);

  useEffect(() => {
    if (authState.user && authState.isAuthenticated) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [authState.user, authState.isAuthenticated, fetchUnreadCount]);

  const login = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
    window.location.href = `${backendUrl}/api/auth/login`;
  };

  const logout = async () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
    try {
      const res = await fetch(`${backendUrl}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      queryClient.clear();
      dispatch(clearUser());
      localStorage.removeItem('user');
      if (data?.ssoLogoutUrl) {
        window.location.href = data.ssoLogoutUrl;
      } else {
        window.location.href = `${backendUrl}/api/auth/login`;
      }
    } catch (err) {
      console.error('Logout error:', err);
      queryClient.clear();
      dispatch(clearUser());
      localStorage.removeItem('user');
      window.location.href = `${backendUrl}/api/auth/login`;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        login,
        logout,
        unreadCount,
        setUnreadCount,
        fetchUnreadCount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
